// src/contexts/BlogContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  getDocs, 
  getDoc, 
  doc, 
  orderBy, 
  limit, 
  where, 
  startAfter 
} from 'firebase/firestore';

export const BlogContext = createContext();

export function BlogProvider({ children }) {
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Fetch initial posts
  const fetchPosts = useCallback(async (postsPerPage = 6) => {
    try {
      setLoading(true);
      setError(null);

      // Query for published posts, ordered by publishDate desc
      const postsQuery = query(
        collection(db, 'blog_posts'),
        where('status', '==', 'published'),
        orderBy('publishDate', 'desc'),
        limit(postsPerPage)
      );

      const querySnapshot = await getDocs(postsQuery);
      
      if (querySnapshot.empty) {
        setPosts([]);
        setHasMore(false);
      } else {
        const fetchedPosts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          publishDate: doc.data().publishDate?.toDate() // Convert Firestore timestamp to JS Date
        }));
        
        setPosts(fetchedPosts);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length >= postsPerPage);
        
        // Set the first post as featured post if any posts exist
        if (fetchedPosts.length > 0) {
          setFeaturedPost(fetchedPosts[0]);
        }
      }
    } catch (err) {
      console.error("Error fetching blog posts:", err);
      setError("Failed to load blog posts. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch more posts (pagination)
  const fetchMorePosts = useCallback(async (postsPerPage = 6) => {
    if (!lastVisible || !hasMore) return;
    
    try {
      setLoading(true);
      
      const postsQuery = query(
        collection(db, 'blog_posts'),
        where('status', '==', 'published'),
        orderBy('publishDate', 'desc'),
        startAfter(lastVisible),
        limit(postsPerPage)
      );
      
      const querySnapshot = await getDocs(postsQuery);
      
      if (querySnapshot.empty) {
        setHasMore(false);
      } else {
        const morePosts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          publishDate: doc.data().publishDate?.toDate()
        }));
        
        setPosts(prevPosts => [...prevPosts, ...morePosts]);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length >= postsPerPage);
      }
    } catch (err) {
      console.error("Error fetching more blog posts:", err);
      setError("Failed to load more posts. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [lastVisible, hasMore]);

  // Get a single post by ID or slug
  const getPostById = useCallback(async (idOrSlug) => {
    try {
      setLoading(true);
      setError(null);
      
      // First try to fetch by ID directly
      const postRef = doc(db, 'blog_posts', idOrSlug);
      let postSnap = await getDoc(postRef);
      
      // If not found, try to find by slug
      if (!postSnap.exists()) {
        // Query for post with matching slug
        const slugQuery = query(
          collection(db, 'blog_posts'),
          where('slug', '==', idOrSlug),
          where('status', '==', 'published'),
          limit(1)
        );
        
        const querySnapshot = await getDocs(slugQuery);
        
        if (!querySnapshot.empty) {
          postSnap = querySnapshot.docs[0];
        } else {
          throw new Error('Post not found');
        }
      }
      
      if (postSnap.exists()) {
        const postData = postSnap.data();
        return {
          id: postSnap.id,
          ...postData,
          publishDate: postData.publishDate?.toDate()
        };
      } else {
        throw new Error('Post not found');
      }
    } catch (err) {
      console.error("Error fetching blog post:", err);
      setError("Failed to load blog post. Please try again later.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch initial data on mount
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return (
    <BlogContext.Provider value={{ 
      posts, 
      featuredPost,
      loading, 
      error, 
      hasMore,
      fetchPosts,
      fetchMorePosts,
      getPostById
    }}>
      {children}
    </BlogContext.Provider>
  );
}